import { StytchB2BProvider } from '@stytch/react/b2b';
import { StytchB2BUIClient } from '@stytch/vanilla-js/b2b';
import type React from 'react';
import { useEffect, useState } from 'react';
import { StytchContext } from './StytchContext';
import { getStytchCookieOptions } from './stytchCookieOptions';

interface StytchProviderProps {
  children: React.ReactNode;
}

// Singleton client to avoid multiple Stytch instances in dev HMR
let cachedStytchClient: StytchB2BUIClient | null = null;
let cachedToken: string | null = null;

export const StytchProvider: React.FC<StytchProviderProps> = ({ children }) => {
  // Check if token is available from env vars immediately (for build-time config)
  const envToken = import.meta.env.VITE_STYTCH_PUBLIC_TOKEN;
  const hasEnvToken = !!envToken && envToken !== 'dummy-token-authentication-disabled';

  const [configLoaded, setConfigLoaded] = useState(hasEnvToken); // If env token exists, consider config loaded
  const [configError, setConfigError] = useState<string | null>(null);
  const [stytchClient, setStytchClient] = useState<StytchB2BUIClient | null>(null);

  // Wait for config.js to load (it loads asynchronously)
  useEffect(() => {
    // If we have env token, skip config.js loading
    if (hasEnvToken) {
      return;
    }

    // Check if config is already loaded
    if (window.__CONFIG__?.STYTCH_PUBLIC_TOKEN) {
      setConfigLoaded(true);
      return;
    }

    // Check if config.js failed to load (script error handler sets this)
    let nestedTimeoutId: ReturnType<typeof setTimeout> | null = null;
    const checkConfigError = () => {
      // Check if config.js script tag exists and failed
      const scripts = document.querySelectorAll('script[src="/config.js"]');
      if (scripts.length > 0) {
        // Script exists but config isn't loaded - likely failed
        const hasToken = !!window.__CONFIG__?.STYTCH_PUBLIC_TOKEN;
        if (!hasToken) {
          // Give it a moment, then check again
          nestedTimeoutId = setTimeout(() => {
            if (!window.__CONFIG__?.STYTCH_PUBLIC_TOKEN) {
              setConfigError('Failed to load /config.js from backend server');
              setConfigLoaded(true);
            }
          }, 2000);
        }
      }
    };

    // Poll for config to be loaded (config.js loads asynchronously)
    const checkInterval = setInterval(() => {
      if (window.__CONFIG__?.STYTCH_PUBLIC_TOKEN) {
        setConfigLoaded(true);
        clearInterval(checkInterval);
      }
    }, 100);

    // Check for errors after a delay
    const checkErrorTimeoutId = setTimeout(checkConfigError, 1000);

    // Give up after 5 seconds
    const timeout = setTimeout(() => {
      clearInterval(checkInterval);
      if (!window.__CONFIG__?.STYTCH_PUBLIC_TOKEN) {
        setConfigError('Timeout waiting for /config.js to load');
      }
      setConfigLoaded(true); // Set to true anyway so we can show error
    }, 5000);

    return () => {
      clearInterval(checkInterval);
      clearTimeout(timeout);
      clearTimeout(checkErrorTimeoutId);
      if (nestedTimeoutId) {
        clearTimeout(nestedTimeoutId);
      }
    };
  }, [hasEnvToken]);

  // Get token from config or env
  const stytchPublicToken =
    (typeof window !== 'undefined' && window.__CONFIG__?.STYTCH_PUBLIC_TOKEN) ||
    import.meta.env.VITE_STYTCH_PUBLIC_TOKEN;
  const hasValidStytchPublicToken =
    !!stytchPublicToken && stytchPublicToken !== 'dummy-token-authentication-disabled';

  // Create or update Stytch client when we have a valid token
  useEffect(() => {
    if (hasValidStytchPublicToken && configLoaded) {
      // Create client if token changed or client doesn't exist
      if (!cachedStytchClient || cachedToken !== stytchPublicToken) {
        cachedStytchClient = new StytchB2BUIClient(stytchPublicToken, {
          cookieOptions: getStytchCookieOptions(),
        });
        cachedToken = stytchPublicToken;
      }
      // Always sync state with cached client when we have a valid token and config is loaded
      // This ensures remounts work correctly even when the cached client already exists
      setStytchClient(cachedStytchClient);
      setConfigError(null);
    } else if (configLoaded && !hasValidStytchPublicToken) {
      // Config loaded but no valid token - clear the client if it exists
      if (cachedStytchClient) {
        cachedStytchClient = null;
        cachedToken = null;
        setStytchClient(null);
      }
    }
  }, [stytchPublicToken, configLoaded, hasValidStytchPublicToken]);

  const ready = hasValidStytchPublicToken && configLoaded && !!stytchClient;
  const errorMessage =
    configError ||
    (ready
      ? null
      : 'STYTCH_PUBLIC_TOKEN missing - ensure the backend server is running on port 8083 and /config.js is accessible');

  // Render children without the Stytch provider only after config loading has
  // failed, so routes like /login can show their configuration error UI.
  const ctx = {
    stytch: stytchClient,
    ready,
    error: errorMessage,
  };

  return (
    <StytchContext.Provider value={ctx}>
      {stytchClient ? (
        <StytchB2BProvider stytch={stytchClient}>{children}</StytchB2BProvider>
      ) : configLoaded && !hasValidStytchPublicToken ? (
        children
      ) : null}
    </StytchContext.Provider>
  );
};
